import React, { FC, useState, useRef, useEffect, useContext } from "react";
import "./Bookmarks.css";
import { useSelector, useDispatch } from "react-redux";
import { Loader } from "@progress/kendo-react-indicators";
import { FiMoreVertical } from "react-icons/fi";
import MapContext from "../MapContext";
import { inAndOut } from "ol/easing";
import { BookMarkEdit, NameBookMark } from "./BookMarkUtils";
import { updateLayerVisible, updateLayerOpacity } from "../../redux/setConfigSlice";
import {
    GetBookMark,
    GetBookMarks,
    SaveBookMark,
    DeleteBookMark,
    handleSaveBookmark,
    getSubLayerIds,
    serializeMarkup,
    deserializePoint,
    deserializeMarkup,
    UpdateBookMarkName,
} from "./utils";
import { changeOpacity, getMapLayerById, toggleVisibility } from "../utils/mapUtils";
import { updateGroupVisible } from "../../redux/setConfigSlice";

const Bookmarks = () => {
    const dispatch = useDispatch();
    const mapConfig = useSelector((state) => state.configData.mapConfig);
    const [selectedBookmark, setSelectedBookMark] = useState();
    const [showForm, setShowForm] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [name, setName] = useState("");
    const [validationMessage, setValidationMessage] = useState();
    const [bookmarks, setBookMarks] = useState([]);
    const sideBarOpen = useSelector((state) => state.toolSelect.sideBarOpen);
    const layerOptions = useSelector((state) => state.layerOptions.layerId);
    //Get Map Object
    const { map } = useContext(MapContext);

    //Get list of all layers and their changable properties
    const layerVisProperties = useSelector(
        (state) => state.configData.layerVisProperties
    );

    // Get list of group layers with visible property
    const groupVisProperties = useSelector(
        (state) => state.configData.groupVisProperties
    );

    const fullLayerGraph = useSelector((state) => state.configData.layers);

    // Units to feed to deserialize markup, in order to add measure drawings back to map
    const areaUnit = useSelector((state) => state.toolSelect.measureAreaUnits);
    const distanceUnit = useSelector((state) => state.toolSelect.measureDistanceUnits);
    const configUnits = useSelector((state) => state.configData.mapConfig.map.units);
    const bearingToggle = useSelector((state) => state.toolSelect.bearingToggle);
    const unitArgs = {
        configUnits,
        areaUnits: areaUnit,
        distanceUnits: distanceUnit,
        bearing: bearingToggle,
    };

    const createPayload = () => {
        const layerList = JSON.stringify([groupVisProperties, layerVisProperties]);
        const markup = JSON.stringify(serializeMarkup(map));
        return {
            bookMarkName: name,
            data: {
                siteid: mapConfig.ID,
                mapExent: map.getView().calculateExtent(map.getSize()),
                mapCenter: map.getView().getCenter(),
                zoomLevel: map.getView().getZoom(),
                layerlist: layerList,
                results: "[]",
                markup: markup,
                sidebarOpen: sideBarOpen,
                createdDate: Date.now(),
            },
        };
    };

    //Get Results Object

    //Loading
    useEffect(() => {
        setLoading(true);
        handleGetBookmarks();
    }, []);

    const handleSaveBookmark = async () => {
        if (name.trim() === "" || name == null) {
            setValidationMessage("Name Required");
        } else if (
            bookmarks.map((e) => e.bookMarkName).filter((e) => e === name).length > 0
        ) {
            setValidationMessage("Duplicated Name");
        } else {
            setValidationMessage("");
            const payload = createPayload();
            try {
                await SaveBookMark(payload, payload.bookMarkName);
                handleCancel();
                handleGetBookmarks();
            } catch (error) {
                console.log(error);
            }
        }
    };

    async function handleGetBookmarks() {
        const bookmarks = await GetBookMarks();
        setBookMarks(bookmarks);
        setLoading(false);
    }

    function handleChangeBookmarkName(e) {
        setName(e.value.trim());
    }

    function handleCancel() {
        setValidationMessage("");
        setName("");
        setShowForm(false);
    }

    async function handleOpenBookmark(id) {
        setLoading(true);
        const bookmark = await GetBookMark(id, bookmarks);
        //Set Extent
        map.getView().fit(bookmark.data.mapExent, {
            duration: 750,
            easing: inAndOut,
        });
        //Set Layer List
        const data = JSON.parse(bookmark.data.layerlist);
        const layers = data[1];
        const groups = data[0];
        layers.forEach((l) => {
            // Check if layer has had read permission restricted since bookmark creation
            if (getMapLayerById(map, l.Id) === undefined) return;
            // Update visibility in state
            dispatch(updateLayerVisible([l.Id, l.Visible]));
            // Update visibility on map
            toggleVisibility(map, l.Id, l.Visible);
            // Update opacity in state
            dispatch(updateLayerOpacity([l.Id, l.Opacity]));
            // Update opacity on map
            changeOpacity(map, l.Id, l.Opacity);
        });
        groups.forEach((g) => {
            if (g.Visible) {
                dispatch(updateGroupVisible([g.DisplayName, true]));
            } else {
                dispatch(updateGroupVisible([g.DisplayName, false]));
                // find layers
                const layerIds = getSubLayerIds(fullLayerGraph, g);
                // turn all layers off
                layerIds.forEach((id) => {
                    toggleVisibility(map, id, false);
                });
            }
        });

        //set Markup
        // clear current drawings
        getMapLayerById(map, "drawLayer").getSource().clear();

        const markupData = bookmark.data.markup;
        if (markupData?.length > 0) {
            const markupFeatures = deserializeMarkup(markupData, unitArgs);
            getMapLayerById(map, "drawLayer").getSource().addFeatures(markupFeatures);
        }

        //Set Results

        setLoading(false);
    }

    async function handleDeleteBookmark() {
        await DeleteBookMark(
            selectedBookmark.id ?? selectedBookmark.data.createdDate,
            bookmarks
        );
        setSelectedBookMark();
        handleGetBookmarks();
    }

    async function handleRenameBookmark() {
        if (name.trim() === "" || name == null) {
            setValidationMessage("Name Required");
        } else if (
            bookmarks.map((e) => e.bookMarkName).filter((e) => e === name).length > 0
        ) {
            setValidationMessage("Duplicated Name");
        } else {
            const id = selectedBookmark.id ?? selectedBookmark.data.createdDate;
            const newName = await UpdateBookMarkName(id, bookmarks, name);
            // Update selected bookmark in state
            const newSelected = { ...selectedBookmark };
            newSelected.bookMarkName = newName;
            setSelectedBookMark(() => newSelected);
            // Update bookmarks in state
            const idxToUpdate = bookmarks.findIndex((b) => {
                const idxId = b.id ?? b.data.createdDate;
                return idxId === id;
            });
            const newBookmarks = [...bookmarks];
            newBookmarks[idxToUpdate].bookMarkName = newName;
            setBookMarks(() => newBookmarks);
            handleCancel();
        }
    }

    return (
        <>
            {!selectedBookmark ? (
                // Default bookmarks view. Either list of bookmarks, nothing if no bookmarks
                //  or loading screen.
                <div className="bookmarks" id="bookmarks">
                    <div className="header">
                        <div className="title">
                            <h3>Bookmarks</h3>
                        </div>
                    </div>
                    {isLoading && (
                        <div className="bookmarks-loading">
                            {" "}
                            <Loader type="infinite-spinner" themeColor="tertiary" />
                        </div>
                    )}
                    {showForm ? (
                        // Create new bookmark button
                        <NameBookMark
                            handleCancel={handleCancel}
                            changeBookmarkName={handleChangeBookmarkName}
                            saveNameCallback={handleSaveBookmark}
                            isRename={false}
                            validationMessage={validationMessage}
                        />
                    ) : (
                        // Create new bookmark button
                        <button
                            onClick={() => setShowForm(true)}
                            className="bookmark-action">
                            Bookmark current extent
                        </button>
                    )}
                    {bookmarks?.length > 0 &&
                        bookmarks.map((bookmark) => (
                            <div
                                className="bookmark-item"
                                key={bookmark.data.createdDate + "div"}
                                onClick={(e) => {
                                    setShowForm(false);
                                    handleOpenBookmark(
                                        bookmark.id ?? bookmark.data.createdDate
                                    );
                                }}>
                                <h3>{bookmark.bookMarkName}</h3>
                                <button
                                    className="menu-bookmark"
                                    key={bookmark.data.createdDate + "_button"}
                                    onClick={(e) => {
                                        setSelectedBookMark(bookmark);
                                        e.stopPropagation();
                                    }}>
                                    <FiMoreVertical />
                                </button>
                            </div>
                        ))}
                </div>
            ) : (
                // When a single bookmark is selected
                <div className="bookmarks" id="bookmark">
                    <div className="header">
                        <div className="title">
                            <h3>{selectedBookmark.bookMarkName}</h3>
                            <p>
                                Created:{" "}
                                {new Date(
                                    selectedBookmark.data.createdDate
                                ).toLocaleDateString("en-us", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                })}
                            </p>
                        </div>
                    </div>
                    {showForm ? (
                        <NameBookMark
                            handleCancel={handleCancel}
                            changeBookmarkName={handleChangeBookmarkName}
                            saveNameCallback={handleRenameBookmark}
                            isRename={true}
                            validationMessage={validationMessage}
                        />
                    ) : (
                        <BookMarkEdit
                            setShowForm={setShowForm}
                            handleDeleteBookmark={handleDeleteBookmark}
                            setSelectedBookMark={setSelectedBookMark}
                            selectedBookmark={selectedBookmark}
                        />
                    )}
                </div>
            )}
        </>
    );
};

export default Bookmarks;
